/**
* This theme is designed to show what can go wrong if you assign a primary and
 * secondary color to values very similar to each other. The theme has been modified
 * to use these colors more than is proper.
 *
 * By default the pallette colors are used to determine the color of the text based on contrast automatically.
 * This can be forcefully changed but without contrast they will be hard to read.
 *
 *
 * Colors are modified in the lightThemeMode under the palette property. Notice that many of the colors can be programmatically generated via functions from Material UI.
 *
 * Some useful links for working with themes can be found at:
 * https://themes.material-ui.com/
 * https://material-ui.com/customization/default-theme/ //Will show all the properties you can edit
 * https://material.io/resources/color/#!/?view.left=0&view.right=0&primary.color=c24f00&secondary.color=214563 //great for color selecting
 *
 */

import { ThemeOptions } from '@material-ui/core';
import { defaultTheme, defaultDarkTheme, AlmeTheme } from '../../../models/AlmeTheme';


/**
 * ! @primaryMain is given a value very close to the secondaryMain, this is done to simulate a bad contrast.
 */

const primaryMain = '#89ba84';
const primaryContrast = defaultTheme.palette.getContrastText(primaryMain); //This is where the auto contrast function lives. Use it to get a complementary, contrasting color.

const secondaryMain = '#89be84';
const secondaryContrast = defaultTheme.palette.getContrastText(secondaryMain);

const palette = {
    primary: {
        light: primaryMain,
        main: primaryMain,
        dark: primaryMain,
    },
    secondary: {
        light: secondaryMain,
        main: secondaryMain,
        dark: secondaryMain,
    },
    background: {
        paper: primaryMain,
        default: secondaryMain,
    },
};

const overrides = {
    MuiAvatar: {
        colorDefault: {
            backgroundColor: '#343434',
        },
    },
    MuiFormLabel: {
        root: {
            color: 'rgba(0, 0, 0, 0.65)',
        },
    },
    MuiButton: {
        containedPrimary: {
            '&&': {
                color: `${primaryContrast}`,
            },
        },
        containedSecondary: {
            '&&': {
                color: `${secondaryContrast}`,
            },
        },
    },
};

const lightModeTheme = {
    palette,
    overrides,
} as ThemeOptions;


const primaryMainDark = '#85CBFF';
const primaryContrastDark = defaultDarkTheme.palette.getContrastText(primaryMain);

const secondaryMainDark = '#00CE7C';
const secondaryContrastDark = defaultDarkTheme.palette.getContrastText(secondaryMain);


const darkPalette = {
    type: 'dark',
    primary: {
        light: '#007AFF',
        main: primaryMainDark,
        dark: '#E1F4FF',
    },
    secondary: {
        light: '#09A761',
        main: secondaryMainDark,
        dark: '#82EB96',
    },
};


const darkOverrides = {
    containedPrimary: {
        '&&': {
            color: `${primaryContrastDark}`,
        },
    },
    containedSecondary: {
        '&&': {
            color: `${secondaryContrastDark}`,
        },
    },
};

const darkModeTheme = {
    palette: darkPalette,
    overrides: darkOverrides,
} as ThemeOptions;


export const appThemeMain = {
    lightTheme: lightModeTheme,
    darkTheme: darkModeTheme,
} as AlmeTheme;
